import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { ArrowLeft, Check, Wifi, WifiOff, Package } from "lucide-react";
import type { KdsTicketItem } from "@/types/kds";

interface ExpoOrder {
  orderId: string;
  orderNumber: number;
  orderType: string;
  tableNumber: string | null;
  tabNumber: string | null;
  customerName: string | null;
  items: KdsTicketItem[];
  doneAt: string;
}

export default function KdsExpoScreen() {
  const navigate = useNavigate();
  const { activeRestaurant } = useRestaurantContext();
  const [orders, setOrders] = useState<ExpoOrder[]>([]);
  const [clock, setClock] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const i = setInterval(() => setClock(new Date()), 1000);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { clearInterval(i); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const fetchExpoOrders = useCallback(async () => {
    if (!activeRestaurant) return;

    // Get all kds_ticket_items that are done
    const { data: allItems } = await supabase
      .from("kds_ticket_items")
      .select("*")
      .eq("restaurant_id", activeRestaurant.id)
      .eq("status", "done")
      .order("done_at", { ascending: false });

    if (!allItems || allItems.length === 0) { setOrders([]); return; }

    const items = allItems as KdsTicketItem[];

    // Group by order
    const byOrder = new Map<string, KdsTicketItem[]>();
    for (const item of items) {
      if (!byOrder.has(item.order_id)) byOrder.set(item.order_id, []);
      byOrder.get(item.order_id)!.push(item);
    }

    // Only show orders where ALL items across ALL stations are done
    // Check if there are any non-done items for these orders
    const orderIds = [...byOrder.keys()];
    const { data: pendingItems } = await supabase
      .from("kds_ticket_items")
      .select("order_id")
      .eq("restaurant_id", activeRestaurant.id)
      .in("order_id", orderIds)
      .neq("status", "done");

    const ordersWithPending = new Set((pendingItems || []).map((i: any) => i.order_id));
    const readyOrderIds = orderIds.filter(id => !ordersWithPending.has(id));

    if (readyOrderIds.length === 0) { setOrders([]); return; }

    // Fetch order details
    const { data: orderData } = await supabase
      .from("orders")
      .select("id, order_number, order_type, table_number, tab_number, customer_name")
      .in("id", readyOrderIds)
      .in("status", ["pending", "preparing", "ready"]);

    if (!orderData) { setOrders([]); return; }

    const result: ExpoOrder[] = [];
    for (const o of orderData) {
      const orderItems = byOrder.get(o.id) || [];
      const latestDone = orderItems.reduce((max, i) => (i.done_at && i.done_at > max ? i.done_at : max), "");
      result.push({
        orderId: o.id,
        orderNumber: o.order_number,
        orderType: o.order_type,
        tableNumber: o.table_number,
        tabNumber: o.tab_number,
        customerName: o.customer_name,
        items: orderItems,
        doneAt: latestDone,
      });
    }

    // Filter out orders done more than 30 minutes ago
    const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
    setOrders(result.filter(o => o.doneAt > thirtyMinAgo).sort((a, b) => a.doneAt.localeCompare(b.doneAt)));
  }, [activeRestaurant]);

  useEffect(() => { fetchExpoOrders(); }, [fetchExpoOrders]);

  // Realtime
  useEffect(() => {
    if (!activeRestaurant) return;
    const channel = supabase
      .channel("kds-expo")
      .on("postgres_changes", { event: "*", schema: "public", table: "kds_ticket_items", filter: `restaurant_id=eq.${activeRestaurant.id}` }, () => fetchExpoOrders())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeRestaurant, fetchExpoOrders]);

  const markServed = async (orderId: string) => {
    // Just remove from expo by deleting done items (or we mark them differently)
    // For now, we'll update done items to a "served" marker via deleting the kds records
    // Actually, let's keep it simple: just remove the kds_ticket_items for this order
    if (!activeRestaurant) return;
    
    await supabase.from("kds_events").insert({
      restaurant_id: activeRestaurant.id,
      order_id: orderId,
      production_location_id: orders.find(o => o.orderId === orderId)?.items[0]?.production_location_id || "",
      event_type: "order_served",
      payload: {},
      user_id: (await supabase.auth.getUser()).data.user?.id || null,
    } as any);

    // Delete KDS items for this order (they've been served)
    await supabase
      .from("kds_ticket_items")
      .delete()
      .eq("order_id", orderId)
      .eq("restaurant_id", activeRestaurant.id);

    fetchExpoOrders();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800 border-b border-zinc-700 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/kds/select-station")} className="p-2 rounded-lg hover:bg-zinc-700 transition-colors">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <Package className="w-5 h-5 text-amber-400" />
          <h1 className="text-lg font-bold text-white">Expedição</h1>
          <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded-full font-medium">
            {orders.length} pronto{orders.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {online ? <Wifi className="w-4 h-4 text-emerald-400" /> : <WifiOff className="w-4 h-4 text-red-400" />}
          <span className="text-sm font-mono text-zinc-400 tabular-nums">
            {clock.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>

      {/* Orders grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {orders.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-zinc-500 text-lg">Nenhum pedido pronto para expedição</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map((order) => (
              <div key={order.orderId} className="rounded-xl border-l-4 border-l-emerald-400 bg-zinc-800 shadow-lg overflow-hidden">
                <div className="px-4 py-3 bg-zinc-800/80 border-b border-zinc-700 flex items-center justify-between">
                  <div>
                    <span className="text-lg font-bold text-white">#{order.orderNumber}</span>
                    <span className="text-xs ml-2 px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 uppercase">
                      {order.orderType === "table" ? "Mesa" : order.orderType === "tab" ? "Comanda" : order.orderType}
                    </span>
                  </div>
                </div>
                <div className="px-4 py-2 border-b border-zinc-700/50">
                  <span className="text-sm text-zinc-400">
                    {order.tableNumber ? `Mesa ${order.tableNumber}` : order.tabNumber ? `Comanda ${order.tabNumber}` : order.customerName || ""}
                  </span>
                </div>
                <div className="px-4 py-2 space-y-1">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex items-baseline gap-2 py-0.5">
                      <span className="text-sm font-bold text-white">{item.quantity}x</span>
                      <span className="text-sm text-zinc-300">{item.name_snapshot}</span>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-zinc-700">
                  <button
                    onClick={() => markServed(order.orderId)}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors text-sm"
                  >
                    <Check className="w-4 h-4" />
                    Servido / Entregue
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
